
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { RecentDocuments } from "@/components/dashboard/RecentDocuments";
import { AchievementsSection } from "@/components/dashboard/AchievementsSection";
import { UsageAnalytics } from "@/components/dashboard/UsageAnalytics";
import { ProgressIndicator } from "@/components/dashboard/ProgressIndicator";
import { SmartSuggestionsWrapper } from "@/components/dashboard/SmartSuggestionsWrapper";
import { AssessmentGate } from "@/components/dashboard/AssessmentGate";
import { BusinessAssessmentCard } from "@/components/dashboard/BusinessAssessmentCard";
import { PersonaToggle } from "@/components/personas/PersonaToggle";
import { PersonaBanner } from "@/components/personas/PersonaBanner";
import { PersonaQuickActions } from "@/components/personas/PersonaQuickActions";
import { PersonaProvider } from "@/components/personas/PersonaProvider";
import { PWAInstallPrompt } from "@/components/ui/pwa-install-prompt";
import { FreeTrialBanner } from "@/components/trial/FreeTrialBanner";
import { TrialStatusWidget } from "@/components/trial/TrialStatusWidget";
import { TrialProvider, useTrialContext } from "@/contexts/TrialContext";
import { SupportBubble } from "@/components/SupportBubble";
import { TutorialManager } from "@/components/dashboard/TutorialManager";
import { useUserProgress } from "@/hooks/use-user-progress";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useTutorial } from "@/hooks/use-tutorial";
import { LoadingState } from "@/components/ui/loading-states";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { ReferralQuickAction } from "@/components/dashboard/ReferralQuickAction";
import { EnterpriseWhiteLabelStatus } from "@/components/organizations/EnterpriseWhiteLabelStatus";


const Dashboard = () => {
  const navigate = useNavigate();
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const { progress, isLoading: isProgressLoading, getProgressSteps, refreshProgress } = useUserProgress();
  const { hasActiveSubscription, subscriptionType, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  const { startTutorial } = useTutorial();

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/auth?mode=signin");
          return;
        }

        console.log("User authenticated, checking access...");
        setIsAuthLoading(false);

        // Check if user should see welcome tutorial
        const hasSeenWelcome = localStorage.getItem('has-seen-welcome-tutorial');
        if (!hasSeenWelcome && progress.hasCompletedAssessment) {
          setTimeout(() => {
            startTutorial('dashboard-overview');
          }, 1000);
        }
      } catch (error) {
        console.error("Error in checkUser:", error);
        navigate("/plan-selection");
      }
    };

    checkUser();
  }, [navigate, startTutorial, progress.hasCompletedAssessment]);

  const handleAssessmentComplete = () => {
    refreshProgress();
  };

  const handleStartTrial = () => {
    navigate('/plan-selection');
  };

  const handleUpgrade = () => {
    navigate('/plan-selection');
  };

  if (isAuthLoading || isProgressLoading || isSubscriptionLoading) {
    return <LoadingState message="Loading your dashboard..." />;
  }

  return (
    <ErrorBoundary>
      <PersonaProvider>
        <TrialProvider>
          <DashboardContentWithTrial 
            onAssessmentComplete={handleAssessmentComplete}
            onStartTrial={handleStartTrial}
            onUpgrade={handleUpgrade}
          />
        </TrialProvider>
      </PersonaProvider>
    </ErrorBoundary>
  );
};

const DashboardContentWithTrial = ({ onAssessmentComplete, onStartTrial, onUpgrade }: {
  onAssessmentComplete: () => void;
  onStartTrial: () => void;
  onUpgrade: () => void;
}) => {
  const { hasActiveTrial, hasExpiredTrial, isLoading: trialLoading } = useTrialContext();
  const { hasActiveSubscription, subscriptionType, isLoading: subscriptionLoading } = useSubscriptionStatus();
  const { progress, isLoading: progressLoading, getProgressSteps } = useUserProgress();
  const navigate = useNavigate();

  if (trialLoading || subscriptionLoading || progressLoading) {
    return <LoadingState message="Loading your dashboard..." />;
  }

  // Check if user has any active access
  const hasActiveAccess = hasActiveSubscription || hasActiveTrial;
  
  // If trial has expired and no active subscription, show upgrade screen
  if (hasExpiredTrial && !hasActiveSubscription) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-8">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
              <DashboardHeader />
              <PersonaToggle />
            </div>
          </div>

          <Card className="p-8 text-center bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
            <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-orange-600" />
            <h1 className="text-2xl font-bold mb-4 text-orange-800">Trial Expired</h1>
            <p className="text-lg text-orange-700 mb-6">
              Your 24-hour free trial has ended. Upgrade now to continue using CSO Agent.
            </p>
            <Button 
              onClick={() => navigate('/plan-selection')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Choose Your Plan
            </Button>
          </Card>
        </div>
        
        <PWAInstallPrompt />
        <SupportBubble hasLifetime={subscriptionType === 'lifetime'} />
      </div>
    );
  }

  // If no active access and no trial, redirect to plan selection
  if (!hasActiveAccess && !hasExpiredTrial) {
    console.log('No active access, redirecting to plan selection');
    navigate('/plan-selection');
    return <LoadingState message="Redirecting..." />;
  }

  // Regular dashboard for users with active access
  const RegularDashboard = () => (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-4">
            <DashboardHeader />
            <PersonaToggle />
          </div>
          <PersonaBanner />
          <FreeTrialBanner 
            onStartTrial={onStartTrial}
            onUpgrade={onUpgrade}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
            <div data-tutorial="quick-actions">
              <PersonaQuickActions />
            </div>
            <SmartSuggestionsWrapper />
            <div data-tutorial="documents">
              <RecentDocuments />
            </div>
          </div>

          <div className="space-y-4 md:space-y-6 lg:space-y-8">
            <ReferralQuickAction />
            <TrialStatusWidget />
            
            <EnterpriseWhiteLabelStatus 
              subscriptionType={subscriptionType || 'none'}
              hasActiveSubscription={hasActiveSubscription}
            />
            
            <div data-tutorial="business-assessment">
              <BusinessAssessmentCard />
            </div>
            {progress.isNewUser && (
              <ProgressIndicator steps={getProgressSteps()} />
            )}
            <AchievementsSection />
            <UsageAnalytics />
          </div>
        </div>
      </div>
      
      <PWAInstallPrompt />
      <SupportBubble hasLifetime={subscriptionType === 'lifetime'} />
      <TutorialManager />
    </div>
  );

  return (
    <AssessmentGate onAssessmentComplete={onAssessmentComplete}>
      <RegularDashboard />
    </AssessmentGate>
  );
};

export default Dashboard;
